// Interceptor de autenticación — agrega el token y sessionId a cada request.
// Además reintenta una vez ante un 401 forzando un refresh de token (evita loops
// con el header X-Auth-Retry).
import { HttpErrorResponse, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, switchMap, throwError } from 'rxjs';
import { AuthService } from '../../infrastructure/services/auth/auth.service';
import { API_ENDPOINTS } from '../../infrastructure/config/api.config';

const RETRY_HEADER = 'X-Auth-Retry';

// Las peticiones de autenticación no deben pasar por la lógica de token,
// de lo contrario fetchToken() se llamaría a sí mismo de forma infinita.
function isAuthEndpoint(req: HttpRequest<unknown>): boolean {
  return req.url.includes(API_ENDPOINTS.auth.token);
}

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);

  // Dejar pasar directamente los endpoints de autenticación (evita recursión)
  if (isAuthEndpoint(req)) {
    return next(req);
  }

  const withAuth$ = authService.isTokenValid()
    ? next(attachAuthHeaders(req, authService))
    : authService.fetchToken().pipe(switchMap(() => next(attachAuthHeaders(req, authService))));

  return withAuth$.pipe(
    catchError((error: HttpErrorResponse) => {
      // 401 → el token pudo haber sido invalidado en el backend: forzar un
      // refresh y reintentar una única vez (marcado con X-Auth-Retry).
      if (error.status === 401 && !req.headers.has(RETRY_HEADER)) {
        return authService.fetchToken().pipe(
          switchMap(() => {
            const retryReq = attachAuthHeaders(req, authService).clone({
              setHeaders: { [RETRY_HEADER]: 'true' },
            });
            return next(retryReq);
          }),
        );
      }
      return throwError(() => error);
    }),
  );
};

/** Adjunta los headers de autorización/sesión al request */
function attachAuthHeaders(
  req: HttpRequest<unknown>,
  authService: AuthService,
): HttpRequest<unknown> {
  const token = authService.getAccessToken();

  if (!token) return req;

  return req.clone({
    setHeaders: {
      Authorization: `Bearer ${token}`,
      SESSION_ID: authService.getSessionId(),
      Accept: '*/*',
    },
  });
}
